import { useEffect, useId, useState } from 'react';

import { Button, Divider } from '@ds/core';
import { Alert, Modal, Tooltip } from '@ds/feedback';
import { FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { CAMPOS_DINAMICOS, contarCampos, preencherCorpo } from '@/comuns/documentos';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { AssinantesDoModelo, CategoriaDoModelo, ModeloDeDocumento } from '@/data/tipos';
import { VisorDeDocumento } from './VisorDeDocumento';

export interface ModeloModalProps {
    alvo: ModeloDeDocumento | 'novo' | null;
    onClose: () => void;
    onSalvo: () => void;
    /**
     * Categoria com que o modelo NOVO nasce.
     *
     * Configurações › Modelos de atestados abre este mesmo modal: nascer como
     * "Consentimento" ali obrigaria a trocar a categoria toda vez.
     */
    categoriaInicial?: CategoriaDoModelo;
}

const CATEGORIAS: CategoriaDoModelo[] = ['Consentimento', 'Contrato', 'Formulário', 'Orientação', 'Atestado', 'Prescrição'];
const ASSINANTES: AssinantesDoModelo[] = ['Paciente', 'Paciente + profissional', 'Paciente + clínica', 'Não requer assinatura'];

/** Dados de exemplo da pré-visualização — os mesmos que o protótipo usava. */
const EXEMPLO = {
    paciente: 'Clara Ribeiro',
    cpf: '315.772.070-00',
    procedimento: 'Toxina botulínica',
    valorCentavos: 210000,
    data: '08/09/2026',
    profissional: 'Ana Prado',
    clinica: 'Vecchia — Jardins',
};

/** Atestado e prescrição são emitidos pela clínica: não pedem assinatura da paciente. */
function assinantesDaCategoria(categoria: CategoriaDoModelo): AssinantesDoModelo {
    return categoria === 'Atestado' || categoria === 'Prescrição' ? 'Não requer assinatura' : 'Paciente + profissional';
}

export function ModeloModal({ alvo, onClose, onSalvo, categoriaInicial = 'Consentimento' }: ModeloModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idCategoria = useId();
    const idAssinantes = useId();
    const idCorpo = useId();

    const [nome, setNome] = useState('');
    const [categoria, setCategoria] = useState<CategoriaDoModelo>(categoriaInicial);
    const [assinantes, setAssinantes] = useState<AssinantesDoModelo>(assinantesDaCategoria(categoriaInicial));
    const [corpo, setCorpo] = useState('');
    const [publicado, setPublicado] = useState(false);
    const formSujo = useAlteracaoPendente({ nome, categoria, assinantes, corpo, publicado }, alvo);
    const [previa, setPrevia] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const novo = alvo === 'novo';

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        setPrevia(false);
        if (alvo === 'novo') {
            setNome('');
            setCategoria(categoriaInicial);
            setAssinantes(assinantesDaCategoria(categoriaInicial));
            setCorpo('');
            setPublicado(false);
            return;
        }
        setNome(alvo.nome);
        setCategoria(alvo.categoria);
        setAssinantes(alvo.assinantes);
        setCorpo(alvo.corpo);
        setPublicado(alvo.publicado);
    }, [alvo, categoriaInicial]);

    if (!alvo) return null;

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarModeloDeDocumento({
                id: novo ? undefined : alvo.id,
                nome, categoria, assinantes, corpo, publicado,
            });
            aviso({
                title: novo ? 'Modelo criado' : 'Modelo atualizado',
                description: `${nome} · ${publicado ? 'publicado' : 'rascunho'}`,
            });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={660}
            title={novo ? 'Novo modelo de documento' : 'Editar modelo'}
            description="Campos entre chaves são preenchidos com os dados do paciente na hora de emitir."
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="ghost" onClick={() => setPrevia((v) => !v)}>
                        {previa ? 'Voltar ao texto' : 'Pré-visualizar'}
                    </Button>
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!nome.trim()}>
                        {novo ? 'Criar modelo' : 'Salvar alterações'}
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {previa ? (
                    <VisorDeDocumento
                        titulo={nome || 'Modelo sem nome'}
                        corpo={preencherCorpo(corpo, EXEMPLO)}
                        assinantes={assinantes}
                        apoio="Pré-visualização com dados de exemplo"
                    />
                ) : (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <FormField label="Nome do modelo" required htmlFor={idNome} style={{ gridColumn: '1 / -1' }}>
                                <Input id={idNome} value={nome} onChange={(v) => { setNome(v); setErro(null); }} placeholder="Ex.: Termo de consentimento — Peeling" />
                            </FormField>
                            <FormField label="Categoria" htmlFor={idCategoria}>
                                <Select id={idCategoria} value={categoria} onChange={(v) => setCategoria(v as CategoriaDoModelo)} options={CATEGORIAS.map((c) => ({ value: c, label: c }))} />
                            </FormField>
                            <FormField label="Assinantes" htmlFor={idAssinantes} hint="Define as linhas de assinatura do documento">
                                <Select id={idAssinantes} value={assinantes} onChange={(v) => setAssinantes(v as AssinantesDoModelo)} options={ASSINANTES.map((a) => ({ value: a, label: a }))} />
                            </FormField>
                        </div>

                        <Divider label="Campos dinâmicos" spacing={2} />
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {CAMPOS_DINAMICOS.map((campo) => (
                                <Tooltip key={campo.tag} label={campo.descricao}>
                                    <button
                                        type="button" onClick={() => setCorpo((c) => c + campo.tag)}
                                        style={{ height: 26, padding: '0 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-brand)' }}
                                    >
                                        {campo.tag}
                                    </button>
                                </Tooltip>
                            ))}
                        </div>

                        <FormField label="Corpo do documento" htmlFor={idCorpo} hint="Clique em um campo acima para inserir no texto">
                            <Textarea id={idCorpo} rows={9} value={corpo} onChange={setCorpo} placeholder="Eu, {paciente}, declaro estar ciente do procedimento {procedimento}…" />
                        </FormField>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '11px 13px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            <span>{contarCampos(corpo)} campos dinâmicos</span>
                            <span>{corpo.length} caracteres</span>
                        </div>

                        {/* Publicar é o que libera o modelo para emissão: texto pela
                            metade vira documento pela metade, e documento é o que a
                            paciente assina. */}
                        <Switch
                            checked={publicado} onChange={setPublicado} label="Publicado"
                            description="Só modelo publicado aparece na hora de emitir um documento"
                        />
                    </>
                )}
            </div>
        </Modal>
    );
}
